import React, { useEffect, useState } from "react";
import { X, MapPin, Briefcase, ArrowRight, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal } from "@/components/site/primitives";
import { TEAM, BOARD, INTERNS, PAST_INTERNS } from "@/content/team";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { api, API, resolvePhoto } from "@/lib/api";
import { cn } from "@/lib/utils";

const initials = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const Avatar = ({ person, className }) => {
  const src = resolvePhoto(person.photo);
  return src ? (
    <img src={src} alt={`Portrait of ${person.name}`} loading="lazy" className={cn("h-full w-full object-cover object-top", className)} />
  ) : (
    <div className={cn("flex h-full w-full items-center justify-center bg-[var(--wf-farm-green)]", className)} aria-hidden="true">
      <span className="font-serif text-4xl font-semibold text-white">{initials(person.name)}</span>
    </div>
  );
};

const PersonCard = ({ person, onOpen, index }) => (
  <Reveal delay={0.03 * (index % 4)}>
    <button
      type="button"
      onClick={() => onOpen(person)}
      data-testid={`person-card-${person.id}`}
      aria-label={`Read bio for ${person.name}`}
      className="group flex h-full w-full flex-col overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] text-left shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[var(--wf-cream)]">
        <div className="h-full w-full transition-transform duration-[600ms] ease-out motion-safe:group-hover:scale-105">
          <Avatar person={person} />
        </div>
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/45 to-transparent" />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-[var(--wf-maroon)] px-3 py-1 text-[11px] font-semibold text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          Read bio <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-serif text-lg font-semibold text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">{person.name}</h3>
        <p className="mt-1 text-sm font-medium text-[var(--wf-green-text)]">{person.role}</p>
        {person.affiliation && <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{person.affiliation}</p>}
      </div>
    </button>
  </Reveal>
);

const PersonModal = ({ person, open, onOpenChange }) => {
  if (!person) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="person-dialog" className="max-w-3xl overflow-hidden border-[var(--wf-border)] bg-[var(--wf-paper)] p-0 [&>button]:hidden rounded-sm">
        <div className="grid max-h-[86vh] grid-cols-1 sm:grid-cols-[minmax(0,0.9fr)_1.4fr]">
          {/* Photo */}
          <div className="relative hidden min-h-[220px] bg-[var(--wf-cream)] sm:block">
            <Avatar person={person} />
          </div>
          {/* Content */}
          <div className="flex max-h-[86vh] flex-col">
            <div className="flex items-start justify-between gap-3 border-b border-[var(--wf-border)] p-5">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border border-[var(--wf-border)] sm:hidden">
                  <Avatar person={person} />
                </div>
                <div>
                  <DialogTitle className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">{person.name}</DialogTitle>
                  <DialogDescription className="mt-0.5 text-sm font-semibold text-[var(--wf-green-text)]">{person.role}</DialogDescription>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Close bio"
                data-testid="person-dialog-close-button"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--wf-border)] bg-[var(--wf-cream)] text-[var(--wf-ink)] transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            {(person.affiliation || person.location) && (
              <div className="flex flex-wrap gap-2 px-5 pt-4">
                {person.affiliation && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(47,93,58,0.10)] px-3 py-1 text-xs font-medium text-[var(--wf-green-text)]"><Briefcase className="h-3.5 w-3.5" aria-hidden="true" />{person.affiliation}</span>
                )}
                {person.location && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--wf-wheat)] px-3 py-1 text-xs font-medium text-[var(--wf-maroon)]"><MapPin className="h-3.5 w-3.5" aria-hidden="true" />{person.location}</span>
                )}
              </div>
            )}
            <div className="overflow-y-auto px-5 pb-6 pt-4">
              <p className="text-sm leading-7 text-[var(--wf-soil)]">{person.bio}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TeamGrid = () => {
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("all"); // all | staff | board
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    // Static fallback used only if the backend is unavailable. The backend
    // seeds the directory once on startup, so the frontend never seeds.
    const fallback = [
      ...TEAM.map((m, i) => ({ ...m, category: "staff", order: i })),
      ...BOARD.map((m, i) => ({ ...m, category: "board", order: i })),
    ];
    const load = async () => {
      try {
        const { data } = await api.get("/team");
        if (!cancelled) setMembers(Array.isArray(data) && data.length ? data : fallback);
      } catch (e) {
        if (!cancelled) setMembers(fallback);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const loading = members === null;
  const list = members || [];
  const q = query.trim().toLowerCase();
  const match = (p) =>
    !q ||
    p.name.toLowerCase().includes(q) ||
    p.role.toLowerCase().includes(q) ||
    (p.affiliation || "").toLowerCase().includes(q);

  const staff = list.filter((m) => m.category === "staff").filter(match);
  const board = list.filter((m) => m.category === "board").filter(match);
  const staffTotal = list.filter((m) => m.category === "staff").length;
  const boardTotal = list.filter((m) => m.category === "board").length;
  const showStaff = tab === "all" || tab === "staff";
  const showBoard = tab === "all" || tab === "board";
  const interns = INTERNS.filter((p) => !q || p.name.toLowerCase().includes(q) || p.role.toLowerCase().includes(q) || (p.school || "").toLowerCase().includes(q));
  const showInterns = tab === "all" || tab === "interns";
  const total = (showStaff ? staff.length : 0) + (showBoard ? board.length : 0) + (showInterns ? interns.length : 0);

  const tabs = [
    { id: "all", label: `All (${staffTotal + boardTotal + INTERNS.length})` },
    { id: "staff", label: `Staff (${staffTotal})` },
    { id: "board", label: `Board (${boardTotal})` },
    { id: "interns", label: `2026 Interns (${INTERNS.length})` },
  ];

  return (
    <>
      <section id="team" aria-labelledby="team-heading" className="wf-section bg-white">
        <div className="wf-container">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Our people</span>
            <h2 id="team-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Meet the team</h2>
            <p className="mt-3 text-base leading-7 text-muted-foreground">Filter by group or search a name or role, then select anyone to read their full story.</p>
          </Reveal>

          {/* Filter bar */}
          <div className="mx-auto mt-8 flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div role="tablist" aria-label="Filter people" className="inline-flex rounded-full border border-[var(--wf-border)] bg-[var(--wf-cream)] p-1">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`team-tab-${t.id}`}
                  className={cn(
                    "rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    tab === t.id ? "bg-[var(--wf-maroon)] text-[var(--wf-paper)]" : "text-[var(--wf-soil)] hover:text-[var(--wf-maroon)]"
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="relative sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <label htmlFor="team-search" className="sr-only">Search people</label>
              <input
                id="team-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or role…"
                data-testid="team-search-input"
                className="min-h-11 w-full rounded-full border border-[var(--wf-border)] bg-[var(--wf-paper)] py-2.5 pl-9 pr-3 text-sm text-[var(--wf-ink)] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          {loading ? (
            <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4" data-testid="team-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)]">
                  <div className="aspect-[4/5] w-full animate-pulse bg-[var(--wf-cream)]" />
                  <div className="space-y-2 p-5">
                    <div className="h-4 w-2/3 animate-pulse rounded bg-[var(--wf-cream)]" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-[var(--wf-cream)]" />
                  </div>
                </div>
              ))}
            </div>
          ) : total === 0 ? (
            <div data-testid="team-empty-state" className="mx-auto mt-10 max-w-md rounded-sm border border-dashed border-[var(--wf-border)] bg-[var(--wf-cream)] p-10 text-center">
              <p className="font-serif text-lg font-semibold text-[var(--wf-ink)]">No one matches “{query}”.</p>
              <button type="button" onClick={() => { setQuery(""); setTab("all"); }} data-testid="team-clear-button" className="mt-4 inline-flex items-center rounded-full bg-[var(--wf-maroon)] px-4 py-2 text-xs font-semibold text-[var(--wf-paper)] hover:bg-[var(--wf-maroon-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Clear search</button>
            </div>
          ) : (
            <div className="mt-10 space-y-12">
              {showStaff && staff.length > 0 && (
                <div>
                  {tab === "all" && <h3 className="mb-5 font-serif text-xl font-semibold text-[var(--wf-ink)]">Staff</h3>}
                  <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                    {staff.map((p, i) => (<PersonCard key={p.id} person={p} index={i} onOpen={setSelected} />))}
                  </div>
                </div>
              )}
              {showBoard && board.length > 0 && (
                <div>
                  {tab === "all" && <h3 className="mb-5 font-serif text-xl font-semibold text-[var(--wf-ink)]">Board of Directors</h3>}
                  <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                    {board.map((p, i) => (<PersonCard key={p.id} person={p} index={i} onOpen={setSelected} />))}
                  </div>
                </div>
              )}
              {showInterns && interns.length > 0 && (
                <div data-testid="team-interns-section" className="border-t border-[var(--wf-border)] pt-10">
                  {tab === "all" && <h3 className="mb-1 font-serif text-xl font-semibold text-[var(--wf-ink)]">2026 Interns</h3>}
                  <p className="mb-5 text-sm text-muted-foreground">Students learning alongside our team this season. Select anyone to read their story.</p>
                  <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                    {interns.map((p, i) => (<PersonCard key={p.id} person={{ ...p, affiliation: p.school }} index={i} onOpen={setSelected} />))}
                  </div>
                </div>
              )}
              {showInterns && !q && PAST_INTERNS.length > 0 && (
                <div data-testid="team-past-interns-section" className="border-t border-[var(--wf-border)] pt-10">
                  <h3 className="font-serif text-xl font-semibold text-[var(--wf-ink)]">Past Interns</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Seasonal staff and interns who have grown with us over the years.</p>
                  <Accordion type="multiple" className="mt-5 border-t border-[var(--wf-border)]">
                    {PAST_INTERNS.map((yr) => (
                      <AccordionItem key={yr.year} value={yr.year} className="border-[var(--wf-border)]">
                        <AccordionTrigger data-testid={`past-interns-trigger-${yr.year}`} className="py-4 text-left font-serif text-lg font-semibold text-[var(--wf-ink)] hover:text-[var(--wf-maroon)] hover:no-underline">
                          <span className="flex flex-wrap items-baseline gap-x-3">
                            <span>{yr.year}</span>
                            <span className="font-sans text-sm font-medium text-muted-foreground">{yr.people.length} {yr.people.length === 1 ? "person" : "people"}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent data-testid={`past-interns-content-${yr.year}`} className="pb-6">
                          <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--wf-green-text)]">{yr.title}</h4>
                          {yr.intro.map((para, i) => (
                            <p key={i} className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground">{para}</p>
                          ))}
                          <ul className="mt-6 grid gap-6 md:grid-cols-2">
                            {yr.people.map((person) => (
                              <li key={`${yr.year}-${person.name}`} className="border-l-2 border-[var(--wf-sage)] pl-4">
                                <p className="font-serif text-base font-semibold text-[var(--wf-ink)]">{person.name}</p>
                                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--wf-green-text)]">{person.role}</p>
                                {person.paragraphs.map((para, i) => (
                                  <p key={i} className="mt-2 text-sm leading-6 text-[var(--wf-soil)]">{para}</p>
                                ))}
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <PersonModal person={selected} open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }} />
    </>
  );
};
